// Motion Analysis tab: per-athlete video library backed by the private
// 'motion-analysis' Supabase Storage bucket (via /api/motion-storage).
//
// Los vídeos ORIGINALES no se tocan nunca ni se descargan desde aquí: sobre
// cada uno se graban correcciones (varias por vídeo) con el analizador, y esas
// correcciones se ven y se descargan desde el modal de correcciones.
import { useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  VideoCameraIcon,
  RectangleStackIcon,
  PencilSquareIcon,
  XMarkIcon,
  FilmIcon,
} from "@heroicons/react/24/outline";
import {
  useFetchAthleteQuery,
  useFetchMotionVideosQuery,
  useDeleteMotionVideoMutation,
  useDeleteMotionCorrectionMutation,
  useSetMotionVideoMetaMutation,
  useUploadMotionFileMutation,
  useFetchExercisesQuery,
} from "../../../store";
import MotionAnalyzer from "../../../components/motion/MotionAnalyzer";
import UploadVideoModal from "../../../components/motion/UploadVideoModal";
import EditVideoDetailsModal from "../../../components/motion/EditVideoDetailsModal";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import { useCoach } from "../../../hooks/useCoach";

// El atributo `download` de un <a> se IGNORA en enlaces cross-origin, y las URLs
// firmadas viven en supabase.co: por eso la descarga se pide al propio Storage
// con `&download=<nombre>`, que responde con Content-Disposition: attachment.
function correctionDownloadUrl(video, correction) {
  const ext = correction.name.split(".").pop();
  const base = (video?.label || "correction").replace(/\.[^.]+$/, "");
  const stamp = correction.createdAt
    ? `-${dayjs(correction.createdAt).format("YYYY-MM-DD-HHmm")}`
    : "";
  const filename = `${base}-correction${stamp}.${ext}`;
  const sep = correction.url.includes("?") ? "&" : "?";
  return `${correction.url}${sep}download=${encodeURIComponent(filename)}`;
}

// "80 kg × 5 @ 75%" con las partes que tenga el vídeo, "—" si no tiene ninguna.
function formatSet({ weight, reps, rmPercent }) {
  const parts = [];
  if (weight != null) parts.push(`${weight} kg`);
  if (reps != null) parts.push(weight != null ? `${reps}` : `${reps} reps`);
  const base = parts.join(" × ");
  const rm = rmPercent != null ? `@ ${rmPercent}%` : "";
  return [base, rm].filter(Boolean).join(" ") || "—";
}

function formatSize(bytes) {
  if (bytes == null) return "—";
  if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// Modal reutilizado por el analizador, el visor y la lista de correcciones.
// size="full" para trabajar con vídeo; size="md" para contenidos cortos.
function FullModal({ title, subtitle, onClose, children, size = "full" }) {
  const panel =
    size === "md"
      ? "max-h-[80vh] w-full max-w-2xl"
      : "h-[97vh] w-[98vw]";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/75 p-2">
      <div className={`flex ${panel} flex-col overflow-hidden rounded-lg bg-white shadow-2xl`}>
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
          <p className="truncate text-sm font-semibold text-gray-900">
            {title} {subtitle && <span className="font-normal text-gray-500">({subtitle})</span>}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div
          className={
            "min-h-0 p-4 " + (size === "md" ? "overflow-y-auto" : "flex-1 overflow-hidden")
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function MotionAnalysisManager() {
  let { id } = useParams();
  const athleteId = Number(id);
  const { data: athleteData, error, isLoading } = useFetchAthleteQuery(id);
  const {
    data: videosData,
    error: videosError,
    isLoading: videosLoading,
    refetch,
  } = useFetchMotionVideosQuery(athleteId);
  const [uploadFile, { isLoading: uploading }] = useUploadMotionFileMutation();
  const [deleteVideo, { isLoading: deleting }] = useDeleteMotionVideoMutation();
  const [deleteCorrection, { isLoading: deletingCorrection }] =
    useDeleteMotionCorrectionMutation();
  const [setVideoMeta, { isLoading: savingDetails }] = useSetMotionVideoMetaMutation();

  const { coach } = useCoach();
  const { data: exercises } = useFetchExercisesQuery(coach?.id, { skip: !coach?.id });
  const exerciseOf = (exerciseId) => exercises?.find((e) => e.id === exerciseId) ?? null;
  const exerciseName = (exerciseId) => exerciseOf(exerciseId)?.name ?? null;

  const [uploadOpen, setUploadOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(null); // vídeo que se está corrigiendo
  const [viewing, setViewing] = useState(null); // vídeo cuyas correcciones se listan
  const [playing, setPlaying] = useState(null); // corrección abierta en el reproductor
  const [toDelete, setToDelete] = useState(null);
  const [correctionToDelete, setCorrectionToDelete] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [message, setMessage] = useState("");

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  const athlete = athleteData[0];
  const athleteName = `${athlete.firstname ?? ""} ${athlete.lastname ?? ""}`.trim();
  const videos = videosData?.videos || [];
  // El modal de correcciones lee siempre la fila fresca de la caché RTKQ.
  const viewingRow = viewing ? videos.find((v) => v.name === viewing.name) || viewing : null;

  // Los metadatos van en la tabla motion_video; el nombre del objeto sólo lleva
  // el timestamp (que es lo que enlaza el vídeo con sus correcciones).
  const handleUpload = async ({ exercise, file, weight, reps, rmPercent }) => {
    setMessage("");
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]+/g, "-");
    try {
      await uploadFile({
        athleteId,
        kind: "video",
        fileName: `${Date.now()}-${safeName}`,
        blob: file,
        contentType: file.type || "video/mp4",
        meta: { exerciseId: exercise.id, weight, reps, rmPercent },
      }).unwrap();
      setMessage(`"${file.name}" uploaded for ${exercise.name}.`);
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteVideo({ athleteId, videoName: toDelete.name }).unwrap();
      setMessage(`"${toDelete.label}" deleted.`);
    } catch (err) {
      setMessage("Delete failed. Please try again.");
    }
    setToDelete(null);
  };

  const handleSaveDetails = async ({ exerciseId, weight, reps, rmPercent }) => {
    if (!editingVideo) return false;
    try {
      await setVideoMeta({
        athleteId,
        videoName: editingVideo.name,
        exerciseId,
        weight,
        reps,
        rmPercent,
      }).unwrap();
      setMessage("Video details updated.");
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleDeleteCorrection = async () => {
    if (!correctionToDelete) return;
    try {
      await deleteCorrection({
        athleteId,
        correctionName: correctionToDelete.name,
      }).unwrap();
      // Si era la corrección abierta en el reproductor, cerrarlo.
      setPlaying((cur) => (cur && cur.name === correctionToDelete.name ? null : cur));
      setMessage("Correction deleted.");
    } catch (err) {
      setMessage("Could not delete the correction. Please try again.");
    }
    setCorrectionToDelete(null);
  };

  // El analizador entrega la grabación cuando el coach pulsa "Save correction".
  const saveCorrection = (video) => async (blob, ext) => {
    await uploadFile({
      athleteId,
      kind: "correction",
      sourceVideo: video.name,
      ext,
      blob,
      contentType: ext === "mp4" ? "video/mp4" : "video/webm",
    }).unwrap();
    refetch();
  };

  return (
    <div className="mt-4 space-y-6">
      <div className="rounded-lg bg-white p-4 shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold">Motion analysis videos of {athlete.firstname}</p>
            <p className="text-sm text-gray-500">
              Upload a video (e.g. a squat filmed from the side), then record corrections on it:
              prepare the bar tracking and drawings whenever you like, hit record when you want
              to start capturing (everything already on screen is included), and talk over it.
              Each recording is saved as a new correction next to the untouched original.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:bg-gray-300"
          >
            <ArrowUpTrayIcon className="h-5 w-5" />
            {uploading ? "Uploading…" : "Upload video"}
          </button>
        </div>

        {message && <p className="mt-2 text-sm text-indigo-700">{message}</p>}
        {videosError && <p className="mt-2 text-sm text-red-600">Could not load the video list.</p>}

        <div className="mt-4 overflow-x-auto">
          {videosLoading ? (
            <p className="text-sm text-gray-500">Loading videos…</p>
          ) : videos.length === 0 ? (
            <div className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-sm text-gray-500">
              No videos uploaded yet.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase text-gray-500">
                  <th className="py-2 pr-4">Video</th>
                  <th className="py-2 pr-4">Exercise</th>
                  <th className="py-2 pr-4">Set</th>
                  <th className="py-2 pr-4">Uploaded</th>
                  <th className="py-2 pr-4">Corrections</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {videos.map((v) => (
                  <tr key={v.name}>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        <FilmIcon className="h-5 w-5 shrink-0 text-gray-400" />
                        <span className="font-medium text-gray-900">{v.label}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-1">
                        {exerciseName(v.exerciseId) ? (
                          <span className="text-gray-900">{exerciseName(v.exerciseId)}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditingVideo(v)}
                          disabled={savingDetails}
                          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
                          title="Edit exercise, weight and reps"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-gray-900">{formatSet(v)}</td>
                    <td className="py-2 pr-4 text-gray-500">
                      {v.createdAt ? dayjs(v.createdAt).format("DD/MM/YYYY HH:mm") : "—"}
                    </td>
                    <td className="py-2 pr-4">
                      {v.corrections?.length ? (
                        <button
                          type="button"
                          onClick={() => setViewing(v)}
                          className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          title="See the correction recordings of this video"
                        >
                          <RectangleStackIcon className="h-4 w-4" />
                          Corrections ({v.corrections.length})
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-0">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setAnalyzing(v)}
                          className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          title="Open the analyzer and record a new correction"
                        >
                          <VideoCameraIcon className="h-4 w-4" />
                          New correction
                        </button>
                        <button
                          type="button"
                          onClick={() => setToDelete(v)}
                          disabled={deleting}
                          className="inline-flex items-center rounded-md bg-white px-2 py-1.5 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-50 disabled:opacity-40"
                          title="Delete video (and its corrections) from storage"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Analyzer / recorder */}
      {analyzing && (
        <FullModal
          title={`New correction — ${analyzing.label}`}
          subtitle={athleteName}
          onClose={() => {
            setAnalyzing(null);
            refetch();
          }}
        >
          <MotionAnalyzer src={analyzing.url} onSaveCorrection={saveCorrection(analyzing)} />
        </FullModal>
      )}

      {/* Correction recordings of a video */}
      {viewingRow && !playing && (
        <FullModal
          title={`Corrections — ${viewingRow.label}`}
          subtitle={athleteName}
          size="md"
          onClose={() => setViewing(null)}
        >
          <div>
            {viewingRow.corrections?.length ? (
              <ul className="divide-y divide-gray-100">
                {viewingRow.corrections.map((c, i) => (
                  <li key={c.name} className="flex flex-wrap items-center gap-3 py-3">
                    <VideoCameraIcon className="h-5 w-5 shrink-0 text-gray-400" />
                    <span className="font-medium text-gray-900">
                      Recording {viewingRow.corrections.length - i}
                    </span>
                    <span className="text-sm text-gray-500">
                      {c.createdAt ? dayjs(c.createdAt).format("DD/MM/YYYY HH:mm") : "—"}
                    </span>
                    <span className="text-sm text-gray-500">{formatSize(c.size)}</span>
                    <div className="ml-auto flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPlaying(c)}
                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => setCorrectionToDelete(c)}
                        disabled={deletingCorrection}
                        className="inline-flex items-center rounded-md bg-white px-2 py-1.5 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-50 disabled:opacity-40"
                        title="Delete this correction recording"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No corrections recorded for this video yet.</p>
            )}
          </div>
        </FullModal>
      )}

      {/* Correction player + download */}
      {playing && (
        <FullModal
          title={`Correction — ${
            playing.createdAt ? dayjs(playing.createdAt).format("DD/MM/YYYY HH:mm") : ""
          }`}
          subtitle={athleteName}
          onClose={() => setPlaying(null)}
        >
          <div className="flex h-full min-h-0 flex-col gap-3">
            <div className="min-h-0 flex-1">
              <video
                src={playing.url}
                controls
                autoPlay
                className="h-full w-full rounded-lg bg-black object-contain"
              />
            </div>
            <div className="flex shrink-0 justify-center gap-2">
              <a
                href={correctionDownloadUrl(viewingRow, playing)}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Download correction
              </a>
              <button
                type="button"
                onClick={() => setPlaying(null)}
                className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Back to corrections
              </button>
              <button
                type="button"
                onClick={() => setCorrectionToDelete(playing)}
                disabled={deletingCorrection}
                className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-50 disabled:opacity-40"
              >
                <TrashIcon className="h-5 w-5" />
                Delete correction
              </button>
            </div>
          </div>
        </FullModal>
      )}

      <EditVideoDetailsModal
        isOpen={!!editingVideo}
        video={editingVideo}
        currentExercise={editingVideo ? exerciseOf(editingVideo.exerciseId) : null}
        onClose={() => setEditingVideo(null)}
        onSave={handleSaveDetails}
        saving={savingDetails}
      />

      <UploadVideoModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
        uploading={uploading}
      />

      <ConfirmDeleteModal
        isOpen={!!correctionToDelete}
        setIsOpen={(open) => !open && setCorrectionToDelete(null)}
        onConfirm={handleDeleteCorrection}
        title="Delete correction"
        message={
          correctionToDelete
            ? `This correction recording${
                correctionToDelete.createdAt
                  ? ` from ${dayjs(correctionToDelete.createdAt).format("DD/MM/YYYY HH:mm")}`
                  : ""
              } will be removed from storage.\nThe original video and its other corrections are kept.`
            : ""
        }
        confirmLabel="Delete"
      />

      <ConfirmDeleteModal
        isOpen={!!toDelete}
        setIsOpen={(open) => !open && setToDelete(null)}
        onConfirm={handleDelete}
        title="Delete video"
        message={
          toDelete
            ? `"${toDelete.label}" and all its corrections will be removed from storage.\nThis action cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
      />
    </div>
  );
}

export default MotionAnalysisManager;
